import * as React from 'react'
import {
    Alert, Box, Button, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle,
    Divider, FormControlLabel, Grid, IconButton, MenuItem, Paper, Stack, Switch,
    TextField, Typography
} from '@mui/material'
import AddRoundedIcon from '@mui/icons-material/AddRounded'
import ArrowDownwardRoundedIcon from '@mui/icons-material/ArrowDownwardRounded'
import ArrowUpwardRoundedIcon from '@mui/icons-material/ArrowUpwardRounded'
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded'
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded'
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded'
import UploadRoundedIcon from '@mui/icons-material/UploadRounded'

const walls = [['left', 'Left'], ['middle', 'Middle'], ['right', 'Right']]
const fieldSx = { '& .MuiOutlinedInput-root': { backgroundColor: 'rgba(17,29,49,.72)' } }

const parsePositions = value => {
    const result = new Set()
    String(value ?? '').split(',').map(part => part.trim()).filter(Boolean).forEach(part => {
        const [a, b = a] = part.split('-').map(Number)
        if (!Number.isFinite(a) || !Number.isFinite(b)) return
        for (let position = Math.max(1, Math.min(a, b)); position <= Math.min(350, Math.max(a, b)); position += 1)
            result.add(position)
    })
    return [...result].sort((a, b) => a - b)
}

const createCondition = () => ({
    enabled: false,
    mode: 'any_above',
    walls: { left: true, middle: true, right: true },
    threshold: '0'
})

const createRule = () => ({
    id: `rift-rule-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    enabled: true,
    name: '',
    walls: { left: true, middle: true, right: true },
    portalLifeMin: '0',
    portalLifeMax: '100',
    setupType: 'automatic',
    presetId: '',
    waves: '1',
    meleeUnitPriority: [],
    rangedUnitPriority: [],
    fillCourtyard: false,
    supportToolIds: [],
    sendCondition: createCondition(),
    retreatCondition: createCondition()
})

const parseIdList = value => Array.from(new Set(String(value ?? '').split(/[\s,;]+/)
    .map(item => item.trim()).filter(item => /^\d+$/.test(item))))

function WallPicker({ value, onChange }) {
    return (
        <Stack direction="row" flexWrap="wrap">
            {walls.map(([key, label]) => (
                <FormControlLabel key={key} control={<Checkbox size="small" checked={value?.[key] !== false}
                    onChange={event => onChange({ ...value, [key]: event.target.checked })} />} label={label} />
            ))}
        </Stack>
    )
}

function ConditionEditor({ title, value, onChange }) {
    const condition = value ?? createCondition()
    return (
        <Paper variant="outlined" sx={{ p: 1.3, borderColor: 'rgba(129,146,178,.22)', background: 'rgba(11,22,39,.25)' }}>
            <Stack spacing={1.1}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography fontWeight={800}>{title}</Typography>
                    <Switch size="small" checked={Boolean(condition.enabled)}
                        onChange={event => onChange({ ...condition, enabled: event.target.checked })} />
                </Stack>
                {condition.enabled ? <>
                    <TextField select size="small" label="Condition" value={condition.mode ?? 'any_above'} sx={fieldSx}
                        onChange={event => onChange({ ...condition, mode: event.target.value })}>
                        <MenuItem value="any_above">Any checked wall is at or above</MenuItem>
                        <MenuItem value="all_below">All checked walls are at or below</MenuItem>
                    </TextField>
                    <WallPicker value={condition.walls} onChange={next => onChange({ ...condition, walls: next })} />
                    <TextField size="small" type="number" label="Wall life threshold (%)" value={condition.threshold ?? '0'}
                        inputProps={{ min: 0, max: 100 }} sx={fieldSx}
                        onChange={event => onChange({ ...condition, threshold: event.target.value })} />
                </> : null}
            </Stack>
        </Paper>
    )
}

function RuleDialog({ open, initialRule, onClose, onSave }) {
    const [rule, setRule] = React.useState(createRule())
    React.useEffect(() => { if (open) setRule(initialRule ? JSON.parse(JSON.stringify(initialRule)) : createRule()) }, [open, initialRule])
    const patch = next => setRule(current => ({ ...current, ...next }))
    const invertedRange = Number(rule.portalLifeMin) > Number(rule.portalLifeMax)
    const noWalls = !walls.some(([key]) => rule.walls?.[key])

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
            <DialogTitle>{initialRule ? 'Edit RIFT rule' : 'Add RIFT rule'}</DialogTitle>
            <DialogContent dividers>
                <Stack spacing={2} sx={{ pt: .5 }}>
                    {(invertedRange || noWalls) ? <Alert severity="warning">
                        {invertedRange ? 'Minimum portal life cannot be higher than maximum. ' : ''}
                        {noWalls ? 'Select at least one wall.' : ''}
                    </Alert> : null}
                    <Grid container spacing={1.5}>
                        <Grid item xs={12} md={8}><TextField fullWidth label="Optional rule name" value={rule.name ?? ''} sx={fieldSx}
                            onChange={event => patch({ name: event.target.value })} /></Grid>
                        <Grid item xs={12} md={4}><Stack direction="row" alignItems="center" justifyContent="space-between">
                            <Typography fontWeight={800}>Rule enabled</Typography><Switch checked={rule.enabled !== false}
                                onChange={event => patch({ enabled: event.target.checked })} /></Stack></Grid>
                    </Grid>
                    <Box><Typography fontWeight={800}>Walls to attack</Typography><WallPicker value={rule.walls} onChange={next => patch({ walls: next })} /></Box>
                    <Grid container spacing={1.5}>
                        <Grid item xs={6}><TextField fullWidth type="number" label="Portal life from (%)" value={rule.portalLifeMin ?? '0'}
                            inputProps={{ min: 0, max: 100 }} sx={fieldSx} onChange={event => patch({ portalLifeMin: event.target.value })} /></Grid>
                        <Grid item xs={6}><TextField fullWidth type="number" label="Portal life to (%)" value={rule.portalLifeMax ?? '100'}
                            inputProps={{ min: 0, max: 100 }} sx={fieldSx} onChange={event => patch({ portalLifeMax: event.target.value })} /></Grid>
                    </Grid>
                    <Divider />
                    <TextField select fullWidth label="Attack setup" value={rule.setupType ?? 'automatic'} sx={fieldSx}
                        onChange={event => patch({ setupType: event.target.value })}>
                        <MenuItem value="automatic">Automatic RIFT setup</MenuItem>
                        <MenuItem value="preset">Own saved attack setup</MenuItem>
                    </TextField>
                    {rule.setupType === 'preset' ?
                        <TextField fullWidth label="Saved attack preset ID" value={rule.presetId ?? ''} sx={fieldSx}
                            onChange={event => patch({ presetId: event.target.value })} /> :
                        <Grid container spacing={1.5}>
                            <Grid item xs={12} md={3}><TextField fullWidth type="number" label="Waves" value={rule.waves ?? '1'}
                                inputProps={{ min: 1, max: 99 }} sx={fieldSx} onChange={event => patch({ waves: event.target.value })} /></Grid>
                            <Grid item xs={12} md={9}><TextField fullWidth label="Melee unit priority (IDs)" value={(rule.meleeUnitPriority ?? []).join(', ')}
                                helperText="Ordered from first choice to last." sx={fieldSx}
                                onChange={event => patch({ meleeUnitPriority: parseIdList(event.target.value) })} /></Grid>
                            <Grid item xs={12} md={9}><TextField fullWidth label="Ranged unit priority (IDs)" value={(rule.rangedUnitPriority ?? []).join(', ')}
                                helperText="Ordered from first choice to last." sx={fieldSx}
                                onChange={event => patch({ rangedUnitPriority: parseIdList(event.target.value) })} /></Grid>
                            <Grid item xs={12} md={3}><FormControlLabel control={<Switch checked={Boolean(rule.fillCourtyard)}
                                onChange={event => patch({ fillCourtyard: event.target.checked })} />} label="Fill courtyard" /></Grid>
                            <Grid item xs={12}><TextField fullWidth label="Support tool IDs (maximum 3)" value={(rule.supportToolIds ?? []).join(', ')}
                                helperText="Unique tools only; unavailable tools are skipped." sx={fieldSx}
                                onChange={event => patch({ supportToolIds: parseIdList(event.target.value).slice(0, 3) })} /></Grid>
                        </Grid>}
                    <Grid container spacing={1.5}>
                        <Grid item xs={12} md={6}><ConditionEditor title="Send condition" value={rule.sendCondition}
                            onChange={next => patch({ sendCondition: next })} /></Grid>
                        <Grid item xs={12} md={6}><ConditionEditor title="Retreat condition" value={rule.retreatCondition}
                            onChange={next => patch({ retreatCondition: next })} /></Grid>
                    </Grid>
                </Stack>
            </DialogContent>
            <DialogActions><Button onClick={onClose}>Cancel</Button><Button variant="contained" disabled={invertedRange || noWalls}
                onClick={() => onSave(rule)}>Save rule</Button></DialogActions>
        </Dialog>
    )
}

export default function RiftCommanderRulesEditor({ commanderWhiteList, commanderRules = [], ruleSets = [], onChange }) {
    const positions = parsePositions(commanderWhiteList)
    const [selected, setSelected] = React.useState(String(positions[0] ?? ''))
    const [dialog, setDialog] = React.useState({ open: false, index: -1, rule: null })
    const fileInput = React.useRef()
    React.useEffect(() => { if (positions.length && !positions.includes(Number(selected))) setSelected(String(positions[0])) }, [commanderWhiteList, positions, selected])
    const entry = commanderRules.find(item => String(item.commanderPosition) === selected)
    const rules = entry?.rules ?? []
    const updateRules = nextRules => {
        const rest = commanderRules.filter(item => String(item.commanderPosition) !== selected)
        onChange?.({ commanderRules: [...rest, { commanderPosition: selected, rules: nextRules }].sort((a, b) => Number(a.commanderPosition) - Number(b.commanderPosition)) })
    }
    const move = (index, delta) => {
        const target = index + delta
        if (target < 0 || target >= rules.length) return
        const next = [...rules]; [next[index], next[target]] = [next[target], next[index]]; updateRules(next)
    }
    const saveRuleSet = () => {
        if (!rules.length) return
        const name = window.prompt('Rule set name', `Commander ${selected}`)
        if (!name?.trim()) return
        onChange?.({ ruleSets: [...ruleSets, { id: `rift-ruleset-${Date.now()}`, name: name.trim(), rules: JSON.parse(JSON.stringify(rules)) }] })
    }
    const exportRuleSets = () => {
        const blob = new Blob([JSON.stringify({ version: 1, ruleSets }, null, 2)], { type: 'application/json' })
        const url = URL.createObjectURL(blob); const anchor = document.createElement('a')
        anchor.href = url; anchor.download = 'rift-rule-sets.json'; anchor.click(); URL.revokeObjectURL(url)
    }
    const importRuleSets = event => {
        const file = event.target.files?.[0]; event.target.value = ''
        if (!file || file.size > 2 * 1024 * 1024) return window.alert('The file must be smaller than 2 MB.')
        file.text().then(text => {
            const payload = JSON.parse(text); const incoming = Array.isArray(payload) ? payload : payload?.ruleSets
            if (!Array.isArray(incoming)) throw new Error('No ruleSets array found')
            onChange?.({ ruleSets: [...ruleSets, ...incoming].slice(0, 100) })
        }).catch(error => window.alert(`Import failed: ${error.message}`))
    }

    if (!positions.length) return <Alert severity="info">Assign commanders to the RIFT portal first. The generic “all” assignment is intentionally not used.</Alert>
    return <Stack spacing={1.5}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} alignItems={{ md: 'center' }}>
            <TextField select size="small" label="RIFT commander" value={selected} sx={{ ...fieldSx, minWidth: 210 }} onChange={event => setSelected(event.target.value)}>
                {positions.map(position => <MenuItem key={position} value={String(position)}>Commander position {position}</MenuItem>)}
            </TextField>
            <Button startIcon={<AddRoundedIcon />} variant="contained" onClick={() => setDialog({ open: true, index: -1, rule: null })}>Add rule</Button>
            <Button onClick={saveRuleSet} disabled={!rules.length}>Save as rule set</Button>
            <Button startIcon={<UploadRoundedIcon />} onClick={() => fileInput.current?.click()}>Import</Button>
            <Button startIcon={<DownloadRoundedIcon />} onClick={exportRuleSets} disabled={!ruleSets.length}>Export</Button>
            <input ref={fileInput} type="file" accept="application/json,.json" hidden onChange={importRuleSets} />
        </Stack>
        {ruleSets.length ? <TextField select size="small" label="Apply saved rule set" value="" sx={fieldSx}
            onChange={event => { const set = ruleSets.find(item => item.id === event.target.value); if (set) updateRules(JSON.parse(JSON.stringify(set.rules ?? []))) }}>
            {ruleSets.map(set => <MenuItem key={set.id} value={set.id}>{set.name} ({set.rules?.length ?? 0} rules)</MenuItem>)}
        </TextField> : null}
        {!rules.length ? <Alert severity="warning">This commander has no rules and will never be sent to the portal.</Alert> : rules.map((rule, index) => (
            <Paper key={rule.id ?? index} variant="outlined" sx={{ p: 1.3, borderColor: 'rgba(129,146,178,.24)', background: 'rgba(14,25,44,.35)' }}>
                <Stack direction="row" spacing={1} alignItems="center">
                    <Switch size="small" checked={rule.enabled !== false} onChange={event => updateRules(rules.map((item, i) => i === index ? { ...item, enabled: event.target.checked } : item))} />
                    <Box sx={{ flex: 1 }}><Typography fontWeight={850}>{rule.name || `Rule ${index + 1}`}</Typography>
                        <Typography variant="caption" color="text.secondary">Portal {rule.portalLifeMin ?? 0}–{rule.portalLifeMax ?? 100}% · {rule.setupType === 'preset' ? 'saved setup' : `${rule.waves ?? 1} automatic wave(s)`}</Typography></Box>
                    <IconButton size="small" onClick={() => move(index, -1)} disabled={index === 0}><ArrowUpwardRoundedIcon fontSize="small" /></IconButton>
                    <IconButton size="small" onClick={() => move(index, 1)} disabled={index === rules.length - 1}><ArrowDownwardRoundedIcon fontSize="small" /></IconButton>
                    <IconButton size="small" onClick={() => updateRules([...rules.slice(0, index + 1), { ...JSON.parse(JSON.stringify(rule)), id: `rift-rule-${Date.now()}` }, ...rules.slice(index + 1)])}><ContentCopyRoundedIcon fontSize="small" /></IconButton>
                    <Button size="small" onClick={() => setDialog({ open: true, index, rule })}>Edit</Button>
                    <IconButton size="small" color="error" onClick={() => updateRules(rules.filter((_, i) => i !== index))}><DeleteOutlineRoundedIcon fontSize="small" /></IconButton>
                </Stack>
            </Paper>
        ))}
        <RuleDialog open={dialog.open} initialRule={dialog.rule} onClose={() => setDialog({ open: false, index: -1, rule: null })}
            onSave={rule => { updateRules(dialog.index < 0 ? [...rules, rule] : rules.map((item, i) => i === dialog.index ? rule : item)); setDialog({ open: false, index: -1, rule: null }) }} />
    </Stack>
}
